import React, { type ReactElement, type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useFilterCountPreview } from '../hooks/use-filter-count-preview';
import { FilterName } from '../types';
import { getTranslation } from '../utils/config';
import { TRANSLATION_SEPARATOR } from '../constants';

type FilterType = { [key: string]: number };
type FilterVariantType = string[];

type FilterMultSelectProps = {
  label: string;
  handleChangeSelection: (values: string[]) => void;
  handleResetFilter: () => void;
  values: FilterType | FilterVariantType;
  selectedValues: undefined | Array<string>;
  filterName: FilterName;
};

export default function FilterMultiSelect({
  label,
  handleChangeSelection,
  handleResetFilter,
  values,
  selectedValues,
  filterName,
}: FilterMultSelectProps): ReactElement {
  const [open, setOpen] = useState<boolean>(false);
  const [localSelection, setLocalSelection] = useState<string[]>(selectedValues || []);
  const [numFound] = useFilterCountPreview(localSelection, filterName, open);
  const selectRef = useRef<HTMLDivElement>(null);

  const [, resultsTransation] = getTranslation(
    numFound === 1 ? 'show_result' : 'show_results',
    TRANSLATION_SEPARATOR,
  ).split(TRANSLATION_SEPARATOR);

  const handleSelect = (e: SyntheticEvent<HTMLInputElement>) => {
    if (!localSelection.includes(e.currentTarget.value) && e.currentTarget.checked) {
      setLocalSelection([...localSelection, e.currentTarget.value]);
    }

    if (localSelection.includes(e.currentTarget.value) && !e.currentTarget.checked) {
      setLocalSelection(localSelection.filter((value) => value !== e.currentTarget.value));
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (open && selectRef.current && !selectRef.current.contains(e.target as Node)) {
      setLocalSelection(selectedValues || []);
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    setLocalSelection(selectedValues || []);
  }, [selectedValues]);

  const handleToggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (open) {
      setLocalSelection(selectedValues || []);
    }
    setOpen(!open);
  };

  const handleClickClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    applyFilters();
  };

  const handleClickReset = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    handleResetFilter();
    setOpen(false);
  };

  const applyFilters = () => {
    handleChangeSelection(localSelection);
    setOpen(false);
  };

  return (
    <div className={`filter-compact__dropdown ${open ? 'open' : ''}`} ref={selectRef}>
      <button
        className="filter-compact__dropdown-toggle"
        type="button"
        id={`dl-${filterName}-dropdown`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleToggleDropdown}
      >
        {label}
      </button>
      <div
        className="df-filter-compact__dropdown-content"
        aria-labelledby={`dl-${filterName}-dropdown`}
      >
        <div className="filter-compact__dropdown-function">
          <a
            className="filter-compact__dropdown-reset"
            href="#"
            title={getTranslation('deselect')}
            onClick={handleClickReset}
          >
            <svg className="material-symbols">
              <use href={materialSymbolsUrl + '#restart_alt_400'}></use>
            </svg>
            {getTranslation('deselect')}
          </a>
        </div>
        <div className="filter-compact__dropdown-overflow-container">
          <div className="filter-compact__dropdown-overflow">
            {Object.keys(values).map((key) => {
              const name = `${filterName}_${key}`;
              return (
                <div key={name} className="filter-compact__dropdown-check">
                  <input
                    id={name}
                    name={name}
                    value={key}
                    type="checkbox"
                    onChange={handleSelect}
                    checked={localSelection?.includes(key)}
                  />
                  <label htmlFor={name}>{key}</label>
                </div>
              );
            })}
          </div>
        </div>
        <div className="filter-compact__dropdown-footer">
          <button
            className="btn btn-tertiary btn-wide hide-dropdown"
            disabled={numFound === 0}
            title={resultsTransation}
            onClick={handleClickClose}
          >
            <span className="btn-text">
              {numFound} {resultsTransation}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
